/* global document, window, console, setTimeout */
import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Text, Switch, Dropdown, makeStyles, SpinButton, Option, Combobox } from "@fluentui/react-components";
import CommonTooltip from "../../common/CommonTooltip";
import { ChevronLeft20Regular, ChevronRight20Regular } from "@fluentui/react-icons";
import {
  applyShapePreset,
  removeShape,
  transformShape,
  updateShape,
  updateShapeGroup,
  updateShapeMargin,
  updateShapeTransform,
} from "../../../state/cellStylesSlice";
import { onSelection } from "../../../helpers/selection";
import ColorPickerSubMenu from "../submenus/ColorPickerSubMenu";
import {
  LineTypeDashed,
  LineTypeDotted,
  LineTypeSolid,
  LineTypeSolidMedium,
  LineTypeSolidHeavy,
  ShapeArrowCommon,
  ShapeArrowFull,
  ShapeArrowThin,
  ShapeCircle,
  ShapeRectangle,
  ShapeRoundedRectangle,
  ShapeRoundedThinRectangle,
  ShapeArrowWithoutTail,
  ShapeQuarter,
  ShapeOval,
  ShapeArrowCurved,
  ShapeTriangle,
  ShapeTrapeze1,
  ShapeTrapeze2,
  ShapeTrapeze3,
  ShapeTriangleLong,
  BackArrow,
  FullWidth,
  SmallShape,
  MediumShape,
  FlushLeft,
  FlushBottom,
  OverhangLeft,
  OverhangBottom,
  FlipHorizontal,
  FlipVertical,
  Margins,
  Pyramid,
} from "../../../assets/icons";
import PropTypes from "prop-types";
import { storeLastSetting } from "../../../state/appStateSlice";
import { isRotationEnabled, isFlipXEnabled, isFlipYEnabled } from "../../../helpers/shapeUtils";
import PaddingSubMenuItem from "./subMenuItems/PaddingSubMenuItem";
import { InfoIcon, XIcon } from "@phosphor-icons/react";
import { debounce } from "../../../helpers/debounce";
import TrackedButton from "../../common/TrackedButton";
import { v4 as uuidv4 } from "uuid";
import MultiCellShapeSubMenuItem from "./subMenuItems/MultiCellShapeSubMenuItem";

export const useClasses = makeStyles({
  BackArrow: {
    marginRight: 4,
  },
  Header: {
    fontFamily: "Arial",
    fontSize: 10,
    fontWeight: 700,
    lineGeight: 11.5,
    textAlign: "left",
  },
  PanelButton: {
    justifyContent: "left",
    fontFamily: "Arial",
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: "0.02em",
  },
  IconButton: {
    justifyContent: "center",
    minWidth: 0,
    width: 32,
    fontFamily: "Arial",
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: "0.02em",
  },
  AddText: {
    fontFamily: "Arial",
    fontSize: "8pt",
    fontWeight: 400,
    marginRight: "2px",
  },
  AccordionHeader: {
    "& > button": {
      minHeight: 0,
    },
  },
});

const buttonStyles = {
  selected: { backgroundColor: "#F5F5F5" },
  unselected: { backgroundColor: "transparent" },
};

export const ShapePresetIcon = ({ icon, label }) => (
  <div style={{ position: "relative", display: "inline-flex" }}>
    {icon}
    <span
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        color: "white",
        fontWeight: "bold",
        fontSize: label.length > 1 ? "7px" : "9px",
        lineHeight: 1,
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      {label}
    </span>
  </div>
);

ShapePresetIcon.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
};

export const availableShapes = [
  {
    id: "rectangle",
    type: "RoundedRectangle",
    radius: 0,
    icon: <ShapeRectangle />,
  },
  {
    id: "roundedRectangle",
    type: "RoundedRectangle",
    radius: 25,
    icon: <ShapeRoundedRectangle />,
  },
  {
    id: "circle",
    type: "Circle",
    icon: <ShapeCircle />,
  },
  {
    id: "arrowFull",
    type: "ArrowFull",
    icon: <ShapeArrowFull />,
  },
  {
    id: "arrowThin",
    type: "ArrowThin",
    icon: <ShapeArrowThin />,
  },
  {
    id: "arrowCommon",
    type: "ArrowCommon",
    icon: <ShapeArrowCommon />,
  },
  {
    id: "arrowWithoutTail",
    type: "ArrowWithoutTail",
    icon: <ShapeArrowWithoutTail />,
  },
  {
    id: "arrowCurved",
    type: "CurvedArrow",
    icon: <ShapeArrowCurved />,
  },
  {
    id: "quarter",
    type: "Quarter",
    icon: <ShapeQuarter />,
  },
  {
    id: "roundedThinRectangle",
    type: "RoundedThinRectangle",
    icon: <ShapeRoundedThinRectangle />,
  },
  {
    id: "oval",
    type: "Oval",
    icon: <ShapeOval />,
  },
  {
    id: "trapeze3",
    type: "Trapeze3",
    icon: <ShapeTrapeze3 />,
  },
  {
    id: "trapeze2",
    type: "Trapeze2",
    icon: <ShapeTrapeze2 />,
  },
  {
    id: "trapeze1",
    type: "Trapeze1",
    icon: <ShapeTrapeze1 />,
  },
  {
    id: "triangle",
    type: "Triangle",
    icon: <ShapeTriangle />,
  },
  {
    id: "triangleLong",
    type: "LongTriangle",
    icon: <ShapeTriangleLong />,
  },
];

export function getIconForShapeType(type, radius) {
  const shape = availableShapes.find((s) => {
    if (type === "RoundedRectangle") {
      return s.type === "RoundedRectangle" && (radius > 0 ? s.radius > 0 : !s.radius);
    }
    return s.type === type;
  });
  return shape?.icon;
}

const availableShapeGroups = [
  {
    id: "pyramid",
    type: "pyramid",
    icon: <Pyramid />,
    selectionType: "line",
  },
];

export const lineTypes = [
  {
    id: "solid",
    outline: "solid",
    weight: 1,
    icon: <LineTypeSolid />,
  },
  {
    id: "solidMedium",
    outline: "solid",
    weight: 2,
    icon: <LineTypeSolidMedium />,
  },
  {
    id: "solidHeavy",
    outline: "solid",
    weight: 4,
    icon: <LineTypeSolidHeavy />,
  },
  {
    id: "dashed",
    outline: "dashed",
    weight: 1,
    icon: <LineTypeDashed />,
  },
  {
    id: "dotted",
    outline: "dotted",
    weight: 1,
    icon: <LineTypeDotted />,
  },
  {
    id: "none",
    outline: "none",
  },
];

const positioningOptions = [
  {
    id: "full",
    icon: <FullWidth />,
    to: {
      margin: 0,
      marginPerc: 0,
    },
  },
  {
    id: "medium",
    icon: <MediumShape />,
    to: {
      margin: 3,
      marginPerc: 0,
    },
  },
  {
    id: "small",
    icon: <SmallShape />,
    to: {
      margin: 10,
      marginPerc: 0,
    },
  },
  {
    id: "flushLeft",
    icon: <FlushLeft />,
    to: {
      margin: 5,
      marginPerc: 0,
      marginLeft: 0,
      marginLeftPerc: 0,
    },
  },
  {
    id: "flushBottom",
    icon: <FlushBottom />,
    to: {
      margin: 5,
      marginPerc: 0,
      marginBottom: 0,
      marginBottomPerc: 0,
    },
  },
  {
    id: "overhangLeft",
    icon: <OverhangLeft />,
    to: {
      margin: 3,
      marginPerc: 0,
      marginLeft: -10,
      marginLeftPerc: 0,
    },
  },
  {
    id: "overhangBottom",
    icon: <OverhangBottom />,
    to: {
      margin: 3,
      marginPerc: 0,
      marginBottom: -10,
      marginBottomPerc: 0,
    },
  },
];

const angleShortcuts = [0, 45, 90, 135, 180, 225, 270, 315];

const ShapeMenuItem = ({ isKeyboardSelected = false }) => {
  const classes = useClasses();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [openPanelName, setOpenPanelName] = useState(null);
  const [fillMenuOpen, setFillMenuOpen] = useState(false);
  const [outlineMenuOpen, setOutlineMenuOpen] = useState(false);
  const scrollContainerRef = useRef(null);

  const advancedMenu = useSelector((state) => state.preferences.advancedMenu);
  const structure = useSelector((state) => state.gridd.present.structure);
  const selection = useSelector((state) => state.selection);
  const currentStyle = useSelector(
    (state) =>
      state.gridd.present.cellStyles[
        (
          state.selection.cells[0] ??
          (state.selection.rows.length > 0
            ? state.gridd.present.structure.cells[state.selection.rows[0]]
              ? state.gridd.present.structure.cells[state.selection.rows[0]][0]
              : null
            : state.selection.columns.length > 0 && state.gridd.present.structure.cells[0]
              ? state.gridd.present.structure.cells[0][state.selection.columns[0]]
              : null)
        )?.id
      ]
  );
  const currentStyleSheet = useSelector(
    (state) => state.styleSheet.sheets[state.styleSheet.selectedSheet] ?? state.styleSheet.sheets["default"]
  );
  const lastShapeSettings = useSelector((state) => state.app.lastSettings.shape);

  function scrollLeft() {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -100, behavior: "smooth" });
    }
  }
  function scrollRight() {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 100, behavior: "smooth" });
    }
  }

  function insertShape(type, etc) {
    const accent1Color = currentStyleSheet?.colors?.find((c) => c.name === "accent1")?.value ?? "#053259";

    const shapeSettings = {
      type,
      color: lastShapeSettings?.color ?? accent1Color,
      colorName: lastShapeSettings?.colorName ?? "accent1",
      outline: lastShapeSettings?.outline ?? "none",
      outlineWeight: lastShapeSettings?.outlineWeight ?? 1,
      outlineColor: lastShapeSettings?.outlineColor ?? accent1Color,
      outlineColorName: lastShapeSettings?.outlineColorName ?? "accent1",
      shadow: lastShapeSettings?.shadow ?? 0,
      ...etc,
    };

    onSelection(dispatch, updateShape, { structure, selection }, shapeSettings);
    onSelection(dispatch, updateShapeMargin, { structure, selection }, { zIndex: 1 });
    dispatch(storeLastSetting({ shape: shapeSettings }));
  }

  function resolveShapePreset(preset, stylesheet) {
    const textPreset = stylesheet?.presets?.find((p) => p.name === preset.textPreset);
    const fontColorKey = preset.inverted ? textPreset?.fontColor2 : textPreset?.fontColor;
    return {
      name: preset.name,
      shapeType: preset.shapeType,
      radius: preset.radius,
      shapeColorValue: stylesheet?.colors?.find((c) => c.name === preset.shapeColor)?.value,
      shapeColor: preset.shapeColor,
      outline: preset.outline,
      outlineWeight: preset.outlineWeight,
      outlineColorValue: stylesheet?.colors?.find((c) => c.name === preset.outlineColor)?.value,
      outlineColor: preset.outlineColor,
      shadow: preset.shadow,
      margin: preset.margin,
      marginPerc: preset.marginPerc,
      marginLeft: preset.marginLeft,
      marginLeftPerc: preset.marginLeftPerc,
      marginTop: preset.marginTop,
      marginTopPerc: preset.marginTopPerc,
      marginRight: preset.marginRight,
      marginRightPerc: preset.marginRightPerc,
      marginBottom: preset.marginBottom,
      marginBottomPerc: preset.marginBottomPerc,
      textAlign: preset.textAlign,
      verticalAlign: preset.verticalAlign,
      inverted: preset.inverted,
      textPreset: preset.textPreset,
      fontWeight: textPreset?.fontWeight,
      fontStyle: textPreset?.fontStyle,
      fontSize: textPreset?.fontSize,
      fontFamily: textPreset?.fontFamily,
      fontColorValue: stylesheet?.colors?.find((c) => c.name === fontColorKey)?.value,
      fontColorName: fontColorKey,
      padding: preset.padding,
      paddingLeft: preset.paddingLeft,
      paddingTop: preset.paddingTop,
      paddingRight: preset.paddingRight,
      paddingBottom: preset.paddingBottom,
      paddingPerc: preset.paddingPerc,
    };
  }

  function insertShapePreset(preset) {
    const resolved = resolveShapePreset(preset, currentStyleSheet);
    onSelection(dispatch, applyShapePreset, { structure, selection }, resolved);
  }

  function insertShapeGroup(type) {
    const accent1Color = currentStyleSheet?.colors?.find((c) => c.name === "accent1")?.value ?? "#053259";

    dispatch(
      updateShapeGroup({
        id: uuidv4(),
        type,
        cells:
          selection.rows.length > 0
            ? structure.cells[selection.rows[0]].map((c) => c.id)
            : selection.columns.length > 0
              ? structure.cells.map((row) => row[selection.columns[0]].id)
              : selection.cells.map((c) => c.id),
        fill: accent1Color,
        fillName: "accent1",
        outlineColor: accent1Color,
        outlineColorName: "accent1",
      })
    );
  }

  function getMenuButtons() {
    const hasShapeSelected = currentStyle?.shape;

    if (openPanelName === "fill" && fillMenuOpen) {
      const colorButtons = document.querySelectorAll('[id^="color_"]');
      return colorButtons;
    }

    if (openPanelName === "outline-color" && outlineMenuOpen) {
      const colorButtons = document.querySelectorAll('[id^="color_"]');
      return colorButtons;
    }

    if (!hasShapeSelected) {
      const basicShapesBtns = document.querySelectorAll('[id$="_basic_shape_selection_button"]');
      return basicShapesBtns;
    }

    const mainMenuButtons = document.querySelectorAll('[id$="_panel_button"]');
    return mainMenuButtons;
  }

  useEffect(() => {
    if (!isKeyboardSelected) return;

    const menuButtons = getMenuButtons();
    let currentFocusIndex = 0;

    if (menuButtons.length > 0) {
      if (!((openPanelName === "fill" && fillMenuOpen) || (openPanelName === "outline-color" && outlineMenuOpen))) {
        menuButtons[currentFocusIndex].focus();
      }
    }

    const handleKeyDown = (event) => {
      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          currentFocusIndex = (currentFocusIndex + 1) % menuButtons.length;
          menuButtons[currentFocusIndex].focus();
          break;
        }

        case "ArrowUp": {
          event.preventDefault();
          currentFocusIndex = (currentFocusIndex - 1 + menuButtons.length) % menuButtons.length;
          menuButtons[currentFocusIndex].focus();
          break;
        }

        case "ArrowRight": {
          if ((openPanelName === "fill" && fillMenuOpen) || (openPanelName === "outline-color" && outlineMenuOpen)) {
            event.preventDefault();
            currentFocusIndex = (currentFocusIndex + 1) % menuButtons.length;
            menuButtons[currentFocusIndex].focus();
          }
          break;
        }

        case "ArrowLeft": {
          if ((openPanelName === "fill" && fillMenuOpen) || (openPanelName === "outline-color" && outlineMenuOpen)) {
            event.preventDefault();
            currentFocusIndex = (currentFocusIndex - 1 + menuButtons.length) % menuButtons.length;
            menuButtons[currentFocusIndex].focus();
          }
          break;
        }

        case "Enter": {
          event.preventDefault();
          const button = menuButtons[currentFocusIndex];
          console.log("Enter pressed on button:", button?.id, "in menu:", openPanelName);

          if (button && button.id === "shape_menu_fill_panel_button") {
            setOpenPanelName("fill");
            setFillMenuOpen(true);
            return;
          }

          if (button && button.id === "shape_menu_outline_panel_button") {
            setOpenPanelName("outline-color");
            setOutlineMenuOpen(true);
            return;
          }

          if (button?.id?.startsWith("color_")) {
            console.log("Pressing Enter on color button:", button.id, "menu:", openPanelName);

            button.click();

            if (openPanelName === "fill") {
              setFillMenuOpen(false);
            } else if (openPanelName === "outline-color") {
              setOutlineMenuOpen(false);
            }
            setOpenPanelName(null);
            return;
          }

          // Default click behavior for other buttons
          button.click();
          break;
        }

        case "F4":
        case "Escape": {
          setOpenPanelName(null);
          setFillMenuOpen(false);
          setOutlineMenuOpen(false);
          break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    isKeyboardSelected,
    openPanelName,
    fillMenuOpen,
    outlineMenuOpen,
    dispatch,
    selection,
    structure,
    currentStyleSheet,
  ]);

  useEffect(() => {
    if (!isKeyboardSelected) return;

    if ((openPanelName === "fill" && fillMenuOpen) || (openPanelName === "outline-color" && outlineMenuOpen)) {
      setTimeout(() => {
        const colorButtons = document.querySelectorAll('[id^="color_"]');
        if (colorButtons.length > 0) {
          colorButtons[0].focus();
        }
      }, 100);
    }
  }, [isKeyboardSelected, fillMenuOpen, outlineMenuOpen, openPanelName]);

  return (
    <div style={{ border: isKeyboardSelected ? "2px solid #000000" : "none" }}>
      <div style={{ display: currentStyle?.shape || currentStyle?.shapeGroupId ? "none" : undefined }}>
        <div>{t("menu.shape.preset.select")}</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto auto auto auto",
            gridTemplateRows: "auto auto auto",
          }}
        >
          {currentStyleSheet?.shapePresets?.map((preset) => (
            <CommonTooltip key={preset.name} content={preset.name}>
              <TrackedButton
                id={`shape_menu_preset_${preset.name.replace(/\s/g, "_")}_button`}
                icon={
                  <ShapePresetIcon icon={getIconForShapeType(preset.shapeType, preset.radius)} label={preset.label} />
                }
                appearance="subtle"
                onClick={() => insertShapePreset(preset)}
              />
            </CommonTooltip>
          ))}
        </div>
        <div style={{ marginTop: 4 }}>{t("menu.shape.select")}</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto auto auto auto",
            gridTemplateRows: "auto auto auto",
          }}
        >
          {availableShapes.map((shape) => (
            <CommonTooltip key={shape.id} content={t(`shape.${shape.id}`)}>
              <TrackedButton
                id={`shape_menu_${shape.id}_select_button`}
                icon={shape.icon}
                appearance="subtle"
                onClick={() => insertShape(shape.type, { radius: shape.radius })}
              />
            </CommonTooltip>
          ))}
        </div>
        <div style={{ marginTop: 4 }}>
          {t("menu.shape.group.select")}{" "}
          <CommonTooltip content={t("menu.shape.group.select.tooltip")} showDelay={500}>
            <InfoIcon />
          </CommonTooltip>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto auto auto",
            gridTemplateRows: "auto auto auto",
          }}
        >
          {availableShapeGroups.map((shape) => (
            <CommonTooltip key={shape.id} content={t(`shape.${shape.id}`)}>
              <TrackedButton
                id={`shape_group_menu_${shape.id}_select_button`}
                icon={shape.icon}
                appearance="subtle"
                onClick={() => insertShapeGroup(shape.type)}
                disabled={
                  shape.selectionType === "line"
                    ? !(
                        selection.columns.length === 1 ||
                        selection.rows.length === 1 ||
                        selection.cells.every((c) => c.x === selection.cells[0].x) ||
                        selection.cells.every((c) => c.y === selection.cells[0].y)
                      )
                    : false
                }
              />
            </CommonTooltip>
          ))}
        </div>
      </div>
      <div
        style={{
          display: currentStyle?.shape && openPanelName !== "margin" && !currentStyle?.shapeGroupId ? "grid" : "none",
          gridTemplateRows: "auto auto auto auto",
          gap: 4,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            alignItems: "center",
            marginLeft: 4,
            gap: "4px",
          }}
        >
          <Button
            icon={<ChevronLeft20Regular />}
            appearance="subtle"
            onClick={scrollLeft}
            style={{
              minWidth: 24,
              width: 24,
              height: 24,
              padding: 0,
            }}
          />
          <div
            ref={scrollContainerRef}
            style={{
              display: "flex",
              overflowX: "auto",
              gap: "4px",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::WebkitScrollbar": {
                display: "none",
              },
            }}
          >
            {currentStyleSheet?.shapePresets?.map((preset) => (
              <CommonTooltip key={preset.name} content={preset.name}>
                <TrackedButton
                  id={`shape_menu_change_to_preset_${preset.name.replace(/\s/g, "_")}_button`}
                  icon={
                    <ShapePresetIcon icon={getIconForShapeType(preset.shapeType, preset.radius)} label={preset.label} />
                  }
                  appearance="subtle"
                  style={currentStyle?.shape?.preset === preset.name ? buttonStyles.selected : buttonStyles.unselected}
                  onClick={() => insertShapePreset(preset)}
                />
              </CommonTooltip>
            ))}
            {availableShapes.map((shape) => (
              <CommonTooltip key={shape.id} content={t(`shape.${shape.id}`)}>
                <TrackedButton
                  id={`shape_menu_change_to_${shape.id}_panel_button`}
                  icon={shape.icon}
                  appearance="subtle"
                  style={
                    !currentStyle?.shape?.preset &&
                    currentStyle?.shape?.type === shape.type &&
                    (currentStyle?.shape?.radius === shape.radius || shape.radius === undefined)
                      ? buttonStyles.selected
                      : buttonStyles.unselected
                  }
                  onClick={() =>
                    onSelection(
                      dispatch,
                      updateShape,
                      { structure, selection },
                      {
                        type: shape.type,
                        radius: shape.radius,
                      }
                    )
                  }
                />
              </CommonTooltip>
            ))}
          </div>
          <Button
            icon={<ChevronRight20Regular />}
            appearance="subtle"
            onClick={scrollRight}
            style={{
              minWidth: 24,
              width: 24,
              height: 24,
              padding: 0,
            }}
          />
        </div>

        <div
          style={{ display: "grid", gridTemplateColumns: "auto 1fr", gridTemplateRows: "auto auto auto auto", gap: 4 }}
        >
          <Text style={{ paddingLeft: 8, fontSize: 12, fontWeight: 600 }}>{t("menu.shape.fill")}</Text>
          <Text style={{ paddingLeft: 8, fontSize: 12, fontWeight: 600 }}>{t("menu.shape.outline")}</Text>

          <div style={{ justifyContent: "start" }}>
            <Dropdown
              id="shape_menu_fill_panel_button"
              open={fillMenuOpen}
              onOpenChange={(e, data) => {
                if (!isKeyboardSelected) {
                  setFillMenuOpen(data.open);
                  if (data.open) {
                    setOpenPanelName("fill");
                  } else {
                    setOpenPanelName(null);
                  }
                }
              }}
              width={50}
              style={{ minWidth: 0, padding: 0, alignContent: "center", justifyContent: "center" }}
              input={{ style: { width: 24 } }}
              value={
                <div style={{ width: 15, height: 15, backgroundColor: currentStyle?.shape?.color ?? "#e5e5e5" }} />
              }
              onOptionSelect={(_, data) => {
                const selectedColor = currentStyleSheet?.colors?.find((c) => c.name === data.optionValue);
                if (selectedColor) {
                  onSelection(
                    dispatch,
                    updateShape,
                    { structure, selection },
                    {
                      color: selectedColor.value,
                      colorName: selectedColor.name,
                    }
                  );
                }
              }}
            >
              <ColorPickerSubMenu
                id="shape_fill"
                color={currentStyle?.shape?.color}
                colorName={currentStyle?.shape?.colorName}
                enabled={["nocolor", "background", "base", "diverging", "sequential", "status"]}
                onNewColor={(newColor, newName) => {
                  onSelection(
                    dispatch,
                    updateShape,
                    { structure, selection },
                    {
                      color: newColor,
                      colorName: newName,
                    }
                  );
                  setOutlineMenuOpen(false);
                  setOpenPanelName(null);
                  dispatch(storeLastSetting({ shape: { ...lastShapeSettings, color: newColor, colorName: newName } }));
                }}
              />
            </Dropdown>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 4 }}>
            <Dropdown
              width={50}
              style={{ minWidth: 0, padding: 0, alignContent: "center", justifyContent: "center" }}
              input={{ style: { width: 24 } }}
              value={
                lineTypes.find(
                  (l) => l.outline === currentStyle?.shape?.outline && l.weight === currentStyle?.shape?.outlineWeight
                )?.icon ?? "None"
              }
              selectedOptions={[
                lineTypes.find(
                  (l) => l.outline === currentStyle?.shape?.outline && l.weight === currentStyle?.shape?.outlineWeight
                )?.id,
              ]}
              onOptionSelect={(_, d) => {
                const selected = lineTypes.find((lt) => lt.id === d.optionValue);
                onSelection(
                  dispatch,
                  updateShape,
                  { structure, selection },
                  {
                    outline: selected.outline,
                    outlineWeight: selected.weight ?? 1,
                  }
                );
                dispatch(
                  storeLastSetting({
                    shape: { ...lastShapeSettings, outline: selected.outline },
                  })
                );
              }}
            >
              {lineTypes.map((line) => (
                <Option key={line.id} value={line.id}>
                  <div style={{ width: 16, height: 16 }}>{line.icon}</div> {t("menu.shape.outline." + line.id)}
                </Option>
              ))}
            </Dropdown>

            <Dropdown
              id="shape_menu_outline_panel_button"
              disabled={currentStyle?.shape?.outline === "none"}
              positioning={"below-end"}
              open={outlineMenuOpen}
              onOpenChange={(e, data) => {
                if (!isKeyboardSelected) {
                  setOutlineMenuOpen(data.open);
                  if (data.open) {
                    setOpenPanelName("outline-color");
                  } else {
                    setOpenPanelName(null);
                  }
                }
              }}
              width={50}
              style={{ minWidth: 0, padding: 0, alignContent: "center", justifyContent: "center" }}
              input={{ style: { width: 24 } }}
              value={
                <div
                  style={{
                    width: 15,
                    height: 15,
                    backgroundColor: currentStyle?.shape?.outlineColor ?? "#e5e5e5",
                  }}
                />
              }
              onOptionSelect={(_, data) => {
                const selectedColor = currentStyleSheet?.colors?.find((c) => c.name === data.optionValue);
                if (selectedColor) {
                  onSelection(
                    dispatch,
                    updateShape,
                    { structure, selection },
                    {
                      outlineColor: selectedColor.value,
                      outlineColorName: selectedColor.name,
                    }
                  );
                }
              }}
            >
              <ColorPickerSubMenu
                id="shape_outline"
                color={currentStyle?.shape?.outlineColor}
                colorName={currentStyle?.shape?.outlineColorName}
                enabled={["background", "base", "diverging", "sequential", "status"]}
                onNewColor={(newColor, newName) => {
                  onSelection(
                    dispatch,
                    updateShape,
                    { structure, selection },
                    {
                      outlineColor: newColor,
                      outlineColorName: newName,
                    }
                  );
                  setOutlineMenuOpen(false);
                  setOpenPanelName(null);
                  dispatch(
                    storeLastSetting({
                      shape: { ...lastShapeSettings, outlineColor: newColor, outlineColorName: newName },
                    })
                  );
                }}
              />
            </Dropdown>
          </div>

          <Text style={{ paddingLeft: 8, fontSize: 12, fontWeight: 600 }}>{t("menu.shape.shadow")}</Text>
          <Text style={{ paddingLeft: 8, fontSize: 12, fontWeight: 600 }}>{t("menu.shape.rotation")}</Text>

          {advancedMenu ? (
            <CommonTooltip content={t("menu.shape.shadow")} relationship="label">
              <div style={{ alignContent: "center" }}>
                <SpinButton
                  size="small"
                  style={{ minWidth: 0, width: 50 }}
                  min={0}
                  max={100}
                  step={1}
                  value={(currentStyle?.shape?.shadow ?? 0) * 100}
                  onChange={(_, d) =>
                    onSelection(
                      dispatch,
                      updateShape,
                      { structure, selection },
                      { shadow: (isNaN(d.value) ? parseInt(d.displayValue) : d.value) / 100.0 }
                    )
                  }
                />{" "}
                %
              </div>
            </CommonTooltip>
          ) : (
            <Switch
              id="shape_menu_shadow_panel_button"
              checked={currentStyle?.shape?.shadow > 0}
              onChange={(e, data) => {
                const shadowValue = data.checked ? 0.2 : 0;
                onSelection(dispatch, updateShape, { structure, selection }, { shadow: shadowValue });
                dispatch(storeLastSetting({ shape: { ...lastShapeSettings, shadow: shadowValue } }));
              }}
            />
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto" }}>
            <div style={{ alignContent: "center" }}>
              {advancedMenu ? (
                <SpinButton
                  size="small"
                  style={{ minWidth: 0, width: 50 }}
                  min={0}
                  max={359}
                  value={currentStyle?.shape?.rotation ?? 0}
                  onChange={(_, d) => {
                    onSelection(
                      dispatch,
                      updateShapeTransform,
                      { structure, selection },
                      { rotation: parseInt(isNaN(d.value) ? d.displayValue : d.value) ?? "0" }
                    );
                  }}
                />
              ) : (
                <Combobox
                  size="small"
                  width={50}
                  style={{ minWidth: 0 }}
                  input={{ style: { width: 24 } }}
                  value={currentStyle?.shape?.rotation ?? ""}
                  selectedOptions={[currentStyle?.shape?.rotation]}
                  disabled={!isRotationEnabled(currentStyle?.shape?.type)}
                  onOptionSelect={(_, d) => {
                    onSelection(
                      dispatch,
                      updateShapeTransform,
                      { structure, selection },
                      { rotation: parseInt((isNaN(d.optionValue) ? d.optionText : d.optionValue) ?? "0") }
                    );
                  }}
                  onChange={debounce((e) => {
                    if (e.target?.value) {
                      onSelection(
                        dispatch,
                        updateShapeTransform,
                        { structure, selection },
                        { rotation: parseInt(e.target.value) }
                      );
                    }
                  }, 250)}
                >
                  {angleShortcuts.map((angle) => (
                    <Option key={angle} value={angle}>
                      {angle.toFixed()}
                    </Option>
                  ))}
                </Combobox>
              )}{" "}
              Deg
            </div>

            <CommonTooltip content={t("menu.shape.flipX.tooltip")}>
              <Button
                id="shape_flipx_button"
                className={classes.IconButton}
                appearance="subtle"
                icon={<FlipHorizontal />}
                disabled={!isFlipXEnabled(currentStyle?.shape?.type)}
                onClick={() => {
                  onSelection(
                    dispatch,
                    transformShape,
                    { structure, selection },
                    {
                      flipX: true,
                    }
                  );
                }}
              />
            </CommonTooltip>

            <CommonTooltip content={t("menu.shape.flipY.tooltip")}>
              <Button
                id="shape_flipy_button"
                className={classes.IconButton}
                appearance="subtle"
                icon={<FlipVertical />}
                disabled={!isFlipYEnabled(currentStyle?.shape?.type)}
                onClick={() => {
                  onSelection(
                    dispatch,
                    transformShape,
                    { structure, selection },
                    {
                      flipY: true,
                    }
                  );
                }}
              />
            </CommonTooltip>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateRows: "auto auto", gap: 4 }}>
          <Text style={{ paddingLeft: 8, fontSize: 12, fontWeight: 600 }}>{t("menu.shape.positioning")}</Text>

          <div
            style={{ display: "grid", gridTemplateColumns: "repeat(" + positioningOptions.length + ", 1fr)", gap: 2 }}
          >
            {positioningOptions.map((option) => (
              <CommonTooltip key={option.id} content={t("placement." + option.id)}>
                <Button
                  icon={option.icon}
                  size="small"
                  appearance="subtle"
                  onClick={() => {
                    onSelection(
                      dispatch,
                      updateShapeMargin,
                      { structure, selection },
                      {
                        margin: null,
                        marginPerc: null,
                        marginLeft: null,
                        marginTop: null,
                        marginRight: null,
                        marginBottom: null,
                        marginLeftPerc: null,
                        marginTopPerc: null,
                        marginRightPerc: null,
                        marginBottomPerc: null,
                        ...option.to,
                      }
                    );
                  }}
                />
              </CommonTooltip>
            ))}
          </div>
        </div>

        <div
          style={{
            justifyContent: "left",
            display: "grid",
            gridTemplateColumns: "1fr auto",
          }}
        >
          <Button
            id="margin_panel_button"
            className={classes.PanelButton}
            appearance="subtle"
            icon={<Margins />}
            onClick={() => {
              setOpenPanelName("margin");
            }}
          >
            {t("menu.shape.size")}
          </Button>

          <Button
            id="shape_menu_home_remove_shape_panel_button"
            icon={<XIcon />}
            appearance="subtle"
            className={classes.PanelButton}
            style={{ paddingLeft: 0, paddingRight: 4 }}
            onClick={() => {
              onSelection(dispatch, removeShape, { selection, structure });
            }}
          >
            {t("menu.shape.delete")}
          </Button>
        </div>
      </div>
      <div
        style={{
          display: !currentStyle?.shapeGroupId || openPanelName === "margin" ? "none" : "grid",
          gridTemplateRows: "auto auto auto auto",
          gap: 4,
        }}
      >
        <MultiCellShapeSubMenuItem currentStyle={currentStyle} />
      </div>
      <div
        style={{
          display: openPanelName === "margin" ? "grid" : "none",
          gridTemplateRows: "auto 1fr",
        }}
      >
        <div>
          <Button
            id="margin_back_button"
            icon={<BackArrow />}
            onClick={() => setOpenPanelName(null)}
            appearance="subtle"
            className={classes.BackArrow}
          />
          <Text className={classes.Header}>{t("menu.shape.size.title")}</Text>
          <CommonTooltip content={t("menu.shape.size.tooltip")} showDelay={500}>
            <InfoIcon size={16} />
          </CommonTooltip>
        </div>
        <PaddingSubMenuItem currentStyle={currentStyle} propertyName="margin" objectName="shape" />
      </div>
    </div>
  );
};

ShapeMenuItem.propTypes = {
  isKeyboardSelected: PropTypes.bool,
};

export default ShapeMenuItem;
